class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isOpen = false;
    
    this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';
    
    this.setupEventListeners();
    this.setupAccessibility();
  }

  setupEventListeners() {
    if (!this.input) return;

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    this.input.form.addEventListener('reset', this.onFormReset.bind(this));
  }

  setupAccessibility() {
    if (this.input && this.predictiveSearchResults) {
      const resultsId = this.predictiveSearchResults.id || 'predictive-search-results';
      this.input.setAttribute('aria-owns', resultsId);
      this.input.setAttribute('aria-controls', resultsId);
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      const wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) wrapper.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.toggleResetButton();
      return;
    }

    this.getSearchResults(this.searchTerm);
    this.toggleResetButton();
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    const selectedResult = this.querySelector('[aria-selected="true"] a');
    
    if (!query.length || selectedResult) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    event.preventDefault();
    
    if (this.shouldResetForm()) {
      this.searchTerm = '';
      this.input.value = '';
      this.input.focus();
      this.abortController.abort();
      this.abortController = new AbortController();
      
      this.cachedResults = {};
      
      this.closeResults(true);
      this.toggleResetButton();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('li[role="option"], button.predictive-search__item')
    ).filter(element => element.offsetParent !== null);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    for (let i = 0; i < allVisibleElements.length; i++) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
        break;
      }
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 
        ? 0 
        : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 
        ? allVisibleElements.length - 1 
        : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (activeElement) {
      activeElement.setAttribute('aria-selected', 'true');
      if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
      
      this.input.setAttribute('aria-activedescendant', activeElement.id || '');
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (searchForTextElement && previousTerm && newTerm) {
      const currentText = searchForTextElement.innerText;
      if (currentText.includes(previousTerm)) {
        searchForTextElement.innerText = currentText.replace(previousTerm, newTerm);
      }
    }
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const sectionId = this.getAttribute('data-section-id') || 'predictive-search';
    const predictiveSearchUrl = window.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(`${window.location.origin}${predictiveSearchUrl}`);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const text = await response.text();
      const parser = new DOMParser();
      const resultsMarkup = parser.parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')?.innerHTML || '';

      const allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      allPredictiveSearchInstances.forEach(instance => {
        instance.cachedResults[queryKey] = resultsMarkup;
      });

      this.renderSearchResults(resultsMarkup);
      
    } catch (error) {
      if (error.name === 'AbortError') return;
      
      console.warn('Predictive search error:', error);
      this.close();
    }
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', '');
    if (this.statusElement) {
      this.setLiveRegionText(this.loadingText);
    }
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', 'true');
      this.setLiveRegionResults();
      this.open();
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement && this.statusElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;

    const hasValue = this.input.value.length > 0;
    const isHidden = this.resetButton.classList.contains('hidden');

    if (hasValue && isHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (!hasValue && !isHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  shouldResetForm() {
    return true;
  }
}

customElements.define('predictive-search', PredictiveSearch);
